import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

interface AnalyticsEvent {
  type: string;
  page?: string;
  data?: Record<string, string | number>;
  timestamp: number;
}

/**
 * Cookieless analytics service.
 * Tracks page views and custom events in sessionStorage.
 * No cookies, no fingerprinting, no external services.
 * Data stays in the browser — ready to be sent to a self-hosted endpoint later.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);
  private readonly STORAGE_KEY = 'blog_analytics';

  constructor() {
    if (!this.isBrowser) return;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.trackPageView(e.urlAfterRedirects));
  }

  trackPageView(url: string): void {
    this.push({ type: 'pageview', page: url, timestamp: Date.now() });
  }

  trackEvent(type: string, data?: Record<string, string | number>): void {
    this.push({ type, data, timestamp: Date.now() });
  }

  getEvents(): AnalyticsEvent[] {
    if (!this.isBrowser) return [];
    try {
      return JSON.parse(sessionStorage.getItem(this.STORAGE_KEY) ?? '[]');
    } catch {
      return [];
    }
  }

  private push(event: AnalyticsEvent): void {
    if (!this.isBrowser) return;
    const events = this.getEvents();
    events.push(event);
    // Keep last 200 events max to avoid storage issues
    const trimmed = events.slice(-200);
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmed));
  }
}
